'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { supabase } from '@/lib/supabase';
import { AuthLayout, FormInput, AlertMessage, SubmitButton } from '@/components/auth';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Use production URL for email redirect, fallback to current origin for local dev
            const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://waitlistly.live';
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${siteUrl}/reset-password`,
            });

            if (error) throw error;
            setSuccess(true);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to send reset email';
            if (message.includes('Failed to fetch') || message.includes('AuthRetryableFetchError')) {
                setError(
                    'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.'
                );
            } else {
                setError(message);
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <AuthLayout title="Check your email" subtitle="Password reset link sent">
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 text-center">
                    <p className="text-gray-700 text-sm">
                        We sent a reset link to <span className="font-medium text-violet-700">{email}</span>
                    </p>
                    <div className="mt-4 flex flex-col gap-2">
                        <button
                            onClick={() => setSuccess(false)}
                            className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                        >
                            Try different email
                        </button>
                        <Link href="/login" className="text-sm text-gray-500 hover:text-gray-600">
                            ← Back to login
                        </Link>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title="Reset your password" subtitle="Enter your email and we'll send you a reset link">
            <AlertMessage type="error" message={error} />

            <form onSubmit={handleSubmit} className="space-y-5">
                <FormInput
                    id="email"
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                />

                <SubmitButton loading={loading} loadingText="Sending reset link...">
                    Send Reset Link
                </SubmitButton>
            </form>

            {/* Back to Login Link */}
            <div className="mt-8 text-center">
                <Link href="/login" className="text-sm text-gray-600 hover:text-gray-700 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to login
                </Link>
            </div>
        </AuthLayout>
    );
}
